import { Fragment, useState, createContext, useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import { endOfDay, isAfter } from 'date-fns';
import styled from 'styled-components';

import { useDebouncedValue } from '@components/component-helpers';
import { IconJob } from '@components/icons/job';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { FilterConfigField } from '@components/table';
import { FilterData } from '@components/table/filters';
import { FilterActionButtons } from '@components/table/filters/actions-buttons';
import { RenderAppliedFilter } from '@components/table/filters/applied-filter';
import { useFilterTranslate } from '@components/table/filters/filter-utils';
import { FilterListItem } from '@components/table/filters/list-item';
import { MenuProps } from '@components/table/filters/menus/types';
import { FilterSelectionIcon } from '@components/table/filters/selection-icon';
import { CustomAppliedFilter } from '@components/table/filters/shared-types';
import { Text } from '@components/text';
import { abbreviateDate } from '@utils/date';

import { api } from 'api';

import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';
import type { JobState, JobTitleSearchEntry } from 'types/jobs';

const jobTitleFieldToExternalQuery = (filter: FilterData) => {
  switch (filter.comparer) {
    case 'is':
    case 'any_of':
      return { job: filter.value };
    case 'not_is':
    case 'none_of':
      return { job__exclude: filter.value };
    default:
      return {};
  }
};

const valueToJobIdArray = (value?: string | string[]) =>
  value ? (Array.isArray(value) ? value : [String(value)]) : [];

export const JobAppliedFilter: CustomAppliedFilter = ({ filter, config, actions }) => {
  const { t } = useStore();
  const { t: filterT } = useFilterTranslate();

  const ids = valueToJobIdArray(filter.value);

  const jobsQuery = useQuery({
    queryKey: ['job-filter-ids', ids],
    queryFn: () => {
      return api.jobs.search({
        limit: 100,
        queryData: {
          id: ids,
        },
      });
    },
    staleTime: 1000 * 60 * 60,
  });

  const value = jobsQuery.data?.results.map((job) => job.title).join(', ') || t('filters.loading');

  return (
    <RenderAppliedFilter
      actions={actions}
      id={filter.id}
      comparer={{
        label: filterT(`core.comparator.${filter.comparer!}`).replace('...', ''),
        value: filter.comparer!,
      }}
      value={value}
      category={config.label}
      icon={config.icon}
    />
  );
};

type ComputeJobStatusProps = {
  isPublished: boolean;
  isArchived: boolean;
  deadline: string | null;
};

const isJobExpired = (job: ComputeJobStatusProps) => {
  if (!job.deadline) return false;

  const today = new Date();
  const endOfDeadlineDay = endOfDay(new Date(job.deadline));
  return isAfter(today, endOfDeadlineDay);
};

const jobStateToStatus = (job: ComputeJobStatusProps): JobState => {
  if (job.isArchived) return 'archived';
  if (isJobExpired(job)) return 'expired';
  if (job.isPublished) return 'published';
  return 'drafts';
};

// Create a context to pass filter state to ValueRow
const FilterContext = createContext<MenuProps | null>(null);

const ValueRow = ({ job }: { job: JobTitleSearchEntry }) => {
  const { t, lang } = useStore();
  const context = useContext(FilterContext);

  if (!context) return null;

  const { state, actions } = context;
  const value = String(job.id);
  const isMultiComparer = state.comparer === 'any_of' || state.comparer === 'none_of';

  let isSelected = false;
  if (state.value !== undefined) {
    isSelected = isMultiComparer
      ? (state.value as Array<string>).includes(value)
      : state.value === value;
  }

  return (
    <FilterListItem
      key={job.id}
      icon={<FilterSelectionIcon isSelected={isSelected} state={state} />}
      onClick={() => actions.selectValue(value)}
      label={
        <div>
          <Text
            style={{ whiteSpace: 'normal' }}
            font="bold14"
            text={job.title || t('applicants.filters.job.notitle')}
          />
          <Text
            font="regular14"
            color="textSecondary"
            text={`${t('applicants.filters.job.state', t('jobs.tabs.' + jobStateToStatus(job)))}`}
          />
          <Text
            font="regular14"
            color="textSecondary"
            text={t('job.datecreated', abbreviateDate(job.dateCreated, lang))}
          />
        </div>
      }
    />
  );
};

const SearchResultHeading = styled.div({
  height: '2.5rem',
  display: 'flex',
  alignItems: 'center',
});

export const JobSearchMenu = ({ state, actions, config }: MenuProps) => {
  const { t: translateStore } = useStore();
  const { t } = useFilterTranslate();
  const [_search, setSearch] = useState('');
  const search = useDebouncedValue(_search, 350);

  const comparers = ('allowedComparers' in config && config.allowedComparers) || [
    'is',
    'not_is',
    'any_of',
    'none_of',
  ];

  const jobsQuery = useQuery({
    queryKey: ['job-title-search', search],
    queryFn: () => api.jobs.search({ title: search }),
    enabled: Boolean(state.comparer),
    keepPreviousData: true,
  });

  const ids = valueToJobIdArray(state.value);

  const selectedJobsQuery = useQuery({
    queryKey: ['job-filter-selected-ids', ids],
    queryFn: () => api.jobs.search({ limit: 1000, queryData: { id: ids } }),
    enabled: ids.length > 0,
    keepPreviousData: true,
  });

  const selectedJobsNotInResult =
    ids.length > 0
      ? (selectedJobsQuery.data?.results || []).filter((selectedJob) => {
          return !Boolean(jobsQuery.data?.results.find((job) => job.id === selectedJob.id));
        })
      : [];

  const jobList = [...selectedJobsNotInResult, ...(jobsQuery.data?.results || [])];
  const count = jobsQuery.data?.count || 0;
  const isMultiSelect = state.comparer === 'any_of' || state.comparer === 'none_of';

  if (!state.comparer && comparers.length > 1) {
    return (
      <Fragment>
        {comparers.map((comparer) => (
          <FilterListItem
            key={comparer}
            label={t(`core.comparator.${comparer}`)}
            onClick={() => actions.selectComparer(comparer)}
          />
        ))}
      </Fragment>
    );
  }

  if (!state.comparer) {
    return null;
  }

  return (
    <FilterContext.Provider value={{ state, actions, config }}>
      <Fragment>
        <Stack space={4}>
          <SearchBox onChange={setSearch} autoComplete="off" />
          {jobsQuery.data && (
            <div>
              {count > 8 && (
                <SearchResultHeading>
                  <Text
                    font="bold14"
                    color="textPrimary"
                    caps
                    text={translateStore('applicants.filters.job.showing')}
                  />
                </SearchResultHeading>
              )}
              <Fragment>
                {jobsQuery.isLoading && <Loading />}
                {jobList.map((job) => (
                  <ValueRow key={job.id} job={job} />
                ))}
              </Fragment>
            </div>
          )}
        </Stack>
        <FilterActionButtons
          state={state}
          actions={actions}
          disableApply={
            !state.value ||
            (isMultiSelect && Array.isArray(state.value) && state.value.length === 0)
          }
        />
      </Fragment>
    </FilterContext.Provider>
  );
};

export const useJobTitleFilter = (): FilterConfigField => {
  const { t } = useStore();

  return {
    id: 'jobTitle',
    label: t('applicants.filters.job.title'),
    icon: <IconJob />,
    type: 'custom',
    customMenu: JobSearchMenu,
    customAppliedFilter: JobAppliedFilter,
    allowedComparers: ['is', 'not_is', 'any_of', 'none_of'],
    toExternalQuery: jobTitleFieldToExternalQuery,
  };
};
